import React, { useEffect } from 'react';
import { X, PhoneCall, Award, CheckCircle, GraduationCap } from 'lucide-react';
import { InteractiveHoverButton } from './magicui/InteractiveHoverButton';

import kianFaceImg from '../assets/kian-face-cropped.png';

export function MentorDetailModal({ isOpen, onClose, onBookCall }) {
  // Keyboard Escape Key Trap & Focus Management
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="mentor-detail-title">
      <div className="relative w-full max-w-3xl my-8 rounded-2xl border border-slate-200 bg-white text-slate-900 shadow-2xl dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100 overflow-hidden">
        
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950/50 px-6 py-4">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-amber-500" />
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Dedicated Academic Mentor Profile</span>
          </div>
          <button onClick={onClose} aria-label="Close Profile Modal" className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white transition cursor-pointer">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 sm:p-8 space-y-6">
          {/* Profile Banner */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 p-6 rounded-2xl bg-slate-50 dark:bg-slate-950/60">
            <div className="flex-shrink-0">
              <img
                src={kianFaceImg}
                alt="Kian Ahadi — Founder & Academic Director"
                className="h-36 w-36 sm:h-44 sm:w-44 rounded-full border-4 border-amber-400 object-cover shadow-2xl shadow-amber-500/20 bg-slate-900"
              />
            </div>

            <div className="space-y-2 text-center sm:text-left flex-1">
              <h2 id="mentor-detail-title" className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                Kian Ahadi (Founder & Academic Director)
              </h2>
              <div className="text-xs sm:text-sm text-amber-600 dark:text-amber-400 font-bold uppercase tracking-wider leading-snug">
                B.Sc. Candidate in Psychology, Neuroscience & Behaviour — McMaster University
              </div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 pt-1">
                35+ High School Students Mentored into Top Canadian Science & Medical Programs
              </p>
            </div>
          </div>

          {/* Academic Philosophy */}
          <div className="space-y-3">
            <h3 className="text-base font-bold text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
              <Award className="h-4 w-4 text-amber-500" />
              Neuroscience Active Learning Philosophy
            </h3>
            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
              High school students often fail because traditional tutoring relies on passive textbook re-reading. Kian structures 1-on-1 mentorship around active recall drills, stoichiometric problem-solving frameworks, and university cut-off strategy to lock 90%+ averages.
            </p>
          </div>

          {/* Tracks / Syllabi */}
          <div className="space-y-3">
            <h3 className="text-base font-bold text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-amber-500" />
              Mastery Tracks Covered
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              {[
                'Grade 11 & 12 Chemistry (Stoichiometry, Equilibrium)',
                'Grade 11 & 12 Functions, Advanced Functions & Calculus',
                'High School English Essay Architecture & Vocabulary',
                'SAT & OUAC University Entrance Strategy'
              ].map((track, idx) => (
                <div key={idx} className="p-3.5 rounded-xl bg-slate-100 dark:bg-slate-950/60 text-slate-800 dark:text-slate-200 flex items-center gap-2 font-medium">
                  <div className="h-1.5 w-1.5 rounded-full bg-amber-500 flex-shrink-0"></div>
                  {track}
                </div>
              ))}
            </div>
          </div>

          {/* Direct CTA Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-200 dark:border-slate-800">
            <a href="tel:+14374382820" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-slate-300 bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold text-sm transition dark:border-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-white">
              <PhoneCall className="h-4 w-4 text-amber-500" /> Call Kian Directly: +1 (437) 438-2820
            </a>

            <InteractiveHoverButton onClick={() => { onClose(); onBookCall(); }} className="w-full sm:w-auto">
              Book 15-Min Strategy Call
            </InteractiveHoverButton>
          </div>
        </div>
      </div>
    </div>
  );
}
